export interface NotificationChannelRule {
  value: string;
  label: string;
  title: 'required' | 'none';
  titleLabel: string;
  titleMax: number;
  bodyMax: number;
}

export interface NotificationEventRef {
  code: string;
  label: string;
  variables: string[];
  liveChannels: string[];
  existingChannels: string[];
}

export interface NotificationTemplateReference {
  channels: NotificationChannelRule[];
  events: NotificationEventRef[];
  names: { id: string; name: string }[];
}

export const TEMPLATE_NAME_PATTERN = /^[A-Za-z][A-Za-z0-9 ()&'/-]*$/;
const PLACEHOLDER = /\{\{\s*([A-Za-z][A-Za-z0-9_]*)\s*\}\}/g;

export function extractPlaceholders(text: string): string[] {
  return Array.from(new Set(Array.from(String(text || '').matchAll(PLACEHOLDER)).map((m) => m[1])));
}

export function malformedPlaceholders(text: string): string[] {
  const stripped = String(text || '').replace(PLACEHOLDER, '');
  const bad = stripped.match(/\{+[^{}]*\}*|\}+/g) || [];
  return Array.from(new Set(bad.map((b) => b.trim()).filter(Boolean))).slice(0, 3);
}

export function sanitizeTemplateName(raw: string): string {
  return raw.replace(/[^A-Za-z0-9 ()&'/-]+/g, '').replace(/\s{2,}/g, ' ').replace(/^[^A-Za-z]+/, '').slice(0, 60);
}

export interface TemplateFormState {
  name: string;
  code: string;
  channel: string;
  subject: string;
  body: string;
  isActive: boolean;
}

export type TemplateFieldErrors = Partial<Record<'name' | 'code' | 'channel' | 'subject' | 'body', string>>;

export function validateTemplateForm(
  form: TemplateFormState,
  ref: NotificationTemplateReference | undefined,
  ctx: { editing: boolean; takenNames: string[] },
): TemplateFieldErrors {
  const errors: TemplateFieldErrors = {};
  const name = form.name.trim();
  if (!name) errors.name = 'Template name is required';
  else if (name.length < 3) errors.name = 'At least 3 characters';
  else if (!TEMPLATE_NAME_PATTERN.test(name)) errors.name = "Letters, numbers, spaces and ( ) & ' / - only, starting with a letter";
  else if (ctx.takenNames.includes(name.toLowerCase())) errors.name = 'Another template already uses this name';

  const event = ref?.events.find((e) => e.code === form.code);
  if (!form.code) errors.code = 'Pick the app event this template is sent for';
  else if (ref && !event) errors.code = 'Not an event the app sends';

  const rule = ref?.channels.find((c) => c.value === form.channel);
  if (!form.channel) errors.channel = 'Select a channel';
  else if (!ctx.editing && event?.existingChannels.includes(form.channel)) {
    errors.channel = `A ${rule?.label || form.channel} template for ${form.code} already exists — edit it instead`;
  }

  const subject = form.subject.trim();
  const body = form.body.trim();
  if (rule?.title === 'required') {
    if (!subject) errors.subject = `${rule.titleLabel} is required for ${rule.label}`;
    else if (subject.length > rule.titleMax) errors.subject = `At most ${rule.titleMax} characters for ${rule.label}`;
  }
  if (!body) errors.body = 'Body is required';
  else if (rule && body.length > rule.bodyMax) errors.body = `At most ${rule.bodyMax} characters for ${rule.label}`;

  const check = (text: string, key: 'subject' | 'body') => {
    if (errors[key]) return;
    const malformed = malformedPlaceholders(text);
    if (malformed.length) { errors[key] = `Invalid placeholder ${malformed.join(', ')} — use {{variable}}`; return; }
    if (event) {
      const unknown = extractPlaceholders(text).filter((v) => !event.variables.includes(v));
      if (unknown.length) errors[key] = `Unknown variable ${unknown.map((v) => `{{${v}}}`).join(', ')} — pick from the list below`;
    }
  };
  if (rule?.title === 'required') check(subject, 'subject');
  check(body, 'body');
  return errors;
}
